import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { listSuppliers, createSupplier, updateSupplier, deleteSupplier, Supplier } from './api';
import './inventory-v2.css';
import DataTable from '../../shared/components/DataTable';

const Suppliers: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({ name: '', contact_name: '', phone: '', email: '', address: '', website: '', notes: '' });

  const loadSuppliers = useCallback(() => {
    listSuppliers(sessionToken, true)
      .then(res => setSuppliers(res.suppliers))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [sessionToken]);

  useEffect(() => { loadSuppliers(); }, [loadSuppliers]);

  const resetForm = () => {
    setForm({ name: '', contact_name: '', phone: '', email: '', address: '', website: '', notes: '' });
    setEditId(null);
    setShowForm(false);
  };

  const handleEdit = (s: Supplier) => {
    setForm({
      name: s.name, contact_name: s.contact_name || '', phone: s.phone || '',
      email: s.email || '', address: s.address || '', website: s.website || '', notes: s.notes || '',
    });
    setEditId(s.id);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (editId) {
        await updateSupplier(sessionToken, editId, form);
      } else {
        await createSupplier(sessionToken, form);
      }
      resetForm();
      loadSuppliers();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleDeactivate = async (id: number) => {
    if (!window.confirm('Deactivate this supplier?')) return;
    try {
      await deleteSupplier(sessionToken, id);
      loadSuppliers();
    } catch (e: any) {
      setError(e.message);
    }
  };

  if (loading) return <div className="inv2-loading">Loading...</div>;

  const canWrite = ['Owner', 'Admin', 'Manager'].includes(user?.role || '');

  return (
    <div className="inv2-page">
      <div className="inv2-header">
        <h1>🏢 Suppliers</h1>
        <div className="inv2-header-actions">
          {canWrite && (
            <button onClick={() => { resetForm(); setShowForm(true); }} className="inv2-btn inv2-btn-primary">+ Add Supplier</button>
          )}
        </div>
      </div>

      {error && <div className="inv2-error">{error}</div>}

      {showForm && (
        <div className="inv2-modal-overlay" onClick={resetForm}>
          <div className="inv2-modal" onClick={e => e.stopPropagation()}>
            <h2>{editId ? 'Edit Supplier' : 'New Supplier'}</h2>
            <form onSubmit={handleSubmit} className="inv2-form">
              <div className="inv2-form-grid">
                <div className="inv2-form-group"><label>Name *</label><input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required className="inv2-input" /></div>
                <div className="inv2-form-group"><label>Contact Person</label><input type="text" value={form.contact_name} onChange={e => setForm(p => ({ ...p, contact_name: e.target.value }))} className="inv2-input" /></div>
                <div className="inv2-form-group"><label>Phone</label><input type="text" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="inv2-input" /></div>
                <div className="inv2-form-group"><label>Email</label><input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="inv2-input" /></div>
                <div className="inv2-form-group"><label>Address</label><input type="text" value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} className="inv2-input" /></div>
                <div className="inv2-form-group"><label>Website</label><input type="text" value={form.website} onChange={e => setForm(p => ({ ...p, website: e.target.value }))} className="inv2-input" /></div>
                <div className="inv2-form-group inv2-form-full"><label>Notes</label><textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} className="inv2-textarea" /></div>
              </div>
              <div className="inv2-form-actions">
                <button type="submit" className="inv2-btn inv2-btn-primary">{editId ? 'Update' : 'Create'}</button>
                <button type="button" onClick={resetForm} className="inv2-btn">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="inv2-table-container">
        <DataTable
          rows={suppliers}
          rowKey={(s) => s.id}
          caption="Suppliers"
          moreLabel="Contact detail"
          empty="No suppliers yet"
          rowClass={(s) => (!s.is_active ? 'inv2-row-inactive' : undefined)}
          columns={[
            { key: 'name', header: 'Name', priority: 'primary', render: (s) => <Link to={`/inventory/suppliers/${s.id}`} className="inv2-link">{s.name}</Link> },
            { key: 'items', header: 'Items', priority: 'summary', numeric: true, render: (s) => s.item_count || 0 },
            { key: 'status', header: 'Status', priority: 'summary', render: (s) => (s.is_active ? <span className="inv2-badge-active">Active</span> : <span className="inv2-badge-inactive">Inactive</span>) },
            { key: 'contact', header: 'Contact', priority: 'summary', render: (s) => s.contact_name || '—' },
            { key: 'phone', header: 'Phone', priority: 'detail', render: (s) => s.phone || '—' },
            { key: 'email', header: 'Email', priority: 'detail', render: (s) => s.email || '—' },
          ]}
          actions={canWrite ? (s) => (
            <>
              <button onClick={() => handleEdit(s)} className="inv2-btn inv2-btn-sm">Edit</button>
              {s.is_active && user?.role === 'Owner' && (
                <button onClick={() => handleDeactivate(s.id)} className="inv2-btn inv2-btn-sm inv2-btn-danger">Deactivate</button>
              )}
            </>
          ) : undefined}
        />
      </div>
    </div>
  );
};

export default Suppliers;
